import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import { setMode, setX, setY, calculate, clearResult } from '../store/slices/percentageSlice'
import './PercentagePage.css'

const PercentagePage = ({ tools = [] }) => {
  const dispatch = useDispatch()
  const { mode, x, y, result, formula } = useSelector(state => state.percentage)
  const [lastUpdated, setLastUpdated] = useState(null)

  useEffect(() => {
    setLastUpdated(
      new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    )
  }, [])

  const handleModeChange = (newMode) => {
    dispatch(setMode(newMode))
  }

  const handleXChange = (e) => {
    dispatch(setX(e.target.value))
  }

  const handleYChange = (e) => {
    dispatch(setY(e.target.value))
  }

  const handleCalculate = () => {
    dispatch(calculate())
  }

  const handleClear = () => {
    dispatch(setX(''))
    dispatch(setY(''))
    dispatch(clearResult())
  }

  useEffect(() => {
    if (x && y) {
      dispatch(calculate())
    }
  }, [x, y, mode, dispatch])

  const renderInputs = () => {
    switch (mode) {
      case 'whatIs':
        return (
          <div className="percentage-inputs">
            <div className="input-row">
              <Input
                type="number"
                label="What"
                value={x}
                onChange={handleXChange}
                placeholder="Enter percentage"
                step="0.01"
              />
              <span className="input-symbol">%</span>
              <span className="input-text">of</span>
              <Input
                type="number"
                label="Value"
                value={y}
                onChange={handleYChange}
                placeholder="Enter value"
                step="0.01"
              />
            </div>
          </div>
        )
      case 'whatPercent':
        return (
          <div className="percentage-inputs">
            <div className="input-row">
              <Input
                type="number"
                label="Value"
                value={x}
                onChange={handleXChange}
                placeholder="Enter value"
                step="0.01"
              />
              <span className="input-text">is what % of</span>
              <Input
                type="number"
                label="Total"
                value={y}
                onChange={handleYChange}
                placeholder="Enter total"
                step="0.01"
              />
            </div>
          </div>
        )
      case 'percentChange':
        return (
          <div className="percentage-inputs">
            <div className="input-row">
              <span className="input-text">% change from</span>
              <Input
                type="number"
                label="From"
                value={x}
                onChange={handleXChange}
                placeholder="Enter initial value"
                step="0.01"
              />
              <span className="input-text">to</span>
              <Input
                type="number"
                label="To"
                value={y}
                onChange={handleYChange}
                placeholder="Enter final value"
                step="0.01"
              />
            </div>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <ToolPageTemplate path="/percentage/" tools={tools}>
      <div>
        <section className="percentage-calculator">
          <div className="percentage-controls">
            <div className="mode-tabs">
              <button
                className={`mode-tab ${mode === 'whatIs' ? 'active' : ''}`}
                onClick={() => handleModeChange('whatIs')}
              >
                What is X% of Y?
              </button>
              <button
                className={`mode-tab ${mode === 'whatPercent' ? 'active' : ''}`}
                onClick={() => handleModeChange('whatPercent')}
              >
                X is what % of Y?
              </button>
              <button
                className={`mode-tab ${mode === 'percentChange' ? 'active' : ''}`}
                onClick={() => handleModeChange('percentChange')}
              >
                % Change
              </button>
            </div>

            {renderInputs()}

            <div className="percentage-buttons">
              <Button onClick={handleCalculate} disabled={!x || !y}>
                Calculate
              </Button>
              <Button variant="secondary" onClick={handleClear}>
                Clear
              </Button>
            </div>
          </div>

          {result !== null && (
            <ResultBox title="Result">
              <div className="percentage-result">
                <div className="result-value">
                  {mode === 'whatPercent' || mode === 'percentChange' ? (
                    <span className="result-number">{result.toFixed(2)}%</span>
                  ) : (
                    <span className="result-number">{result.toFixed(2)}</span>
                  )}
                </div>
                {formula && (
                  <div className="result-formula">
                    <strong>Formula:</strong> {formula}
                  </div>
                )}
              </div>
            </ResultBox>
          )}
        </section>

        <section className="tool-guide">
          <div className="guide-section">
            <h2>What Is a Percentage Calculator?</h2>
            <p>A percentage calculator is a tool that works out percentage problems for you — finding a percentage of a number, working out what percent one number is of another, and calculating the percentage increase or decrease between two values. You enter the numbers, and it returns the answer instantly along with the formula it used.</p>
            <p>A percentage is simply a number expressed as a fraction of 100 — the word comes from the Latin <em>per centum</em>, "by the hundred." Saying "25%" is the same as saying 25 out of 100, or the fraction 0.25. Because every percentage shares the same base of 100, percentages let you compare very different quantities on a level playing field, which is why they show up in prices, grades, statistics, and interest rates.</p>
            <p>This calculator runs entirely in your browser, handles decimals and negative numbers, and shows the working so you can check it — useful whether you're double-checking a discount at the till or a figure in a report.</p>
          </div>

          <div className="guide-section">
            <h2>The Three Core Percentage Formulas (With Examples)</h2>
            <p><strong>1. What is X% of Y?</strong> &nbsp;Multiply Y by X, then divide by 100. To find 25% of 200: 200 × 25 ÷ 100 = <strong>50</strong>. To find 15% of 80: 80 × 15 ÷ 100 = <strong>12</strong>.</p>
            <p><strong>2. X is what percent of Y?</strong> &nbsp;Divide X by Y and multiply by 100. So 45 is what percent of 180? 45 ÷ 180 × 100 = <strong>25%</strong>. And 30 out of 40 correct answers is 30 ÷ 40 × 100 = <strong>75%</strong>.</p>
            <p><strong>3. Percentage change (increase or decrease):</strong> &nbsp;Subtract the old value from the new one, divide by the old value, then multiply by 100. Going from 50 to 65 is (65 − 50) ÷ 50 × 100 = <strong>+30%</strong>. Going from 80 to 60 is (60 − 80) ÷ 80 × 100 = <strong>−25%</strong>. A positive answer is an increase; a negative answer is a decrease.</p>
          </div>

          <div className="guide-section">
            <h2>How to Use This Percentage Calculator</h2>
            <div className="guide-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Pick the calculation type</h3>
                  <p>Use the tabs to choose what you're solving: "What is X% of Y?" for a percentage of a number, "X is what % of Y?" to turn a fraction into a percentage, or "% Change" to compare two values.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Enter your numbers</h3>
                  <p>Type your values into the fields. Decimals (like 2.5 or 17.75) and negative numbers are fine — the calculator validates inputs as you go so you don't get a misleading answer from a stray character.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Read the result and the formula</h3>
                  <p>The answer appears instantly, along with the formula used so you can see exactly how it was worked out. That makes it easy to trust the number or spot if you entered a value in the wrong field.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Apply or reset</h3>
                  <p>Use the result in your budget, grade, or report, rounding as needed. Hit Clear to start a fresh calculation without reloading the page.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Everyday Percentage Calculations</h2>
            <div className="use-cases">
              <div className="use-case">
                <h3>Working out a discount</h3>
                <p>For an item priced at $80 with 25% off, the saving is 80 × 25 ÷ 100 = $20, so you pay $60. To get the sale price in one step, multiply by (100 − discount) ÷ 100 — here, 80 × 0.75 = $60.</p>
              </div>
              <div className="use-case">
                <h3>Calculating a tip</h3>
                <p>An 18% tip on a $45 bill is 45 × 18 ÷ 100 = $8.10, for a total of $53.10. For a quick 20%, take 10% (move the decimal one place: $4.50) and double it to $9.</p>
              </div>
              <div className="use-case">
                <h3>Grades and test scores</h3>
                <p>Scoring 42 out of 50 is 42 ÷ 50 × 100 = 84%. To compare two tests, use percentage change: improving from 70% to 84% is a (84 − 70) ÷ 70 × 100 ≈ 20% improvement.</p>
              </div>
              <div className="use-case">
                <h3>Sales tax and price rises</h3>
                <p>Adding 8% tax to a $120 order is 120 × 1.08 = $129.60. The same trick tracks price increases — a rent going from $1,000 to $1,050 rose (1,050 − 1,000) ÷ 1,000 × 100 = 5%.</p>
              </div>
              <div className="use-case">
                <h3>Budgeting by percentage</h3>
                <p>To cap housing at 30% of a $4,000 monthly income: 4,000 × 30 ÷ 100 = $1,200. Splitting income into percentage buckets keeps a budget balanced as earnings change.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3>How do I calculate a percentage of a number?</h3>
                <p>Multiply the number by the percentage, then divide by 100. Example: 20% of 150 = 150 × 20 ÷ 100 = 30. Shortcut: convert 20% to 0.20 and multiply — 150 × 0.20 = 30.</p>
              </div>
              <div className="faq-item">
                <h3>How do I find what percent one number is of another?</h3>
                <p>Divide the first number by the second, then multiply by 100. Example: 18 is what percent of 24? 18 ÷ 24 × 100 = 75%. Use this for test scores, completion rates, or "how much of the total" questions.</p>
              </div>
              <div className="faq-item">
                <h3>How do I calculate percentage increase or decrease?</h3>
                <p>Subtract the original value from the new value, divide by the original value, then multiply by 100. Example: from 200 to 250 = (250 − 200) ÷ 200 × 100 = 25% increase. A negative result means a decrease.</p>
              </div>
              <div className="faq-item">
                <h3>What is the difference between percentage change and percentage points?</h3>
                <p>Percentage points compare the raw difference between two percentages; percentage change compares that difference relative to the starting value. A rate rising from 4% to 6% is a 2 percentage-point increase but a 50% percentage change.</p>
              </div>
              <div className="faq-item">
                <h3>Can I use decimals and negative numbers?</h3>
                <p>Yes. The calculator accepts decimals (2.5, 17.75) and negative numbers, useful for financial figures like losses, rate cuts, or price drops. Results are shown to two decimal places.</p>
              </div>
              <div className="faq-item">
                <h3>How do I quickly work out a discount or tip?</h3>
                <p>For a discount, multiply the price by (100 − discount%) ÷ 100 to get the final price directly. For a tip, find 10% by moving the decimal one place left, then scale it: 10% of $60 = $6, so a 15% tip is $9.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Related Tools</h2>
            <div className="related-tools">
              <Link to="/percentage-increase-calculator/" className="related-tool">
                <div className="tool-icon">Percentage Increase Calculator</div>
                <div className="tool-description">Find the percentage increase between two values</div>
              </Link>
              <Link to="/percentage-decrease-calculator/" className="related-tool">
                <div className="tool-icon">Percentage Decrease Calculator</div>
                <div className="tool-description">Find the percentage decrease between two values</div>
              </Link>
              <Link to="/discount-calculator/" className="related-tool">
                <div className="tool-icon">Discount Calculator</div>
                <div className="tool-description">Calculate sale price and savings from a discount</div>
              </Link>
              <Link to="/tax-calculator/" className="related-tool">
                <div className="tool-icon">Tax Calculator</div>
                <div className="tool-description">Calculate sales tax and total purchase amount</div>
              </Link>
              <Link to="/tip-calculator/" className="related-tool">
                <div className="tool-icon">Tip Calculator</div>
                <div className="tool-description">Split a bill and calculate tip per person</div>
              </Link>
              <Link to="/marks-percentage-calculator/" className="related-tool">
                <div className="tool-icon">Marks Percentage Calculator</div>
                <div className="tool-description">Calculate exam marks percentage and grade</div>
              </Link>
              <Link to="/gpa-calculator/" className="related-tool">
                <div className="tool-icon">GPA / CGPA Calculator</div>
                <div className="tool-description">Calculate semester SGPA and cumulative CGPA</div>
              </Link>
              <Link to="/age/" className="related-tool">
                <div className="tool-icon">Age Calculator</div>
                <div className="tool-description">Work out an exact age in years, months, and days</div>
              </Link>
            </div>
          </div>
        </section>

        <div className="last-updated">
          <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>        </div>
      </div>
    </ToolPageTemplate>
  )
}

export default PercentagePage